import { useState } from "react";

import { useQuery } from "@apollo/client";
import { KeyIcon, MailIcon, UserIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";

import { getSignUpParams } from "@/components/helpers/signup/sign-up-params";
import {
  handleSignUpComplete,
  sendErrorToSentry,
} from "@/components/pieces/signup/handle-sign-up-complete";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignUpMutation, useSignUpMutation } from "@/graphql";
import { GetCSRFQuery } from "@/graphql/cachedVarsQueries";
import { cn } from "@/lib/utils";

interface SignUpFormProps {
  onEmailChange?: (email: string) => void;
  nextPath?: string;
  onCompleted?: (response: SignUpMutation) => void;
  asWorkspaceAdmin?: boolean;
  fields?: string[];
  cta?: string;
}

export function SignUpForm({
  onEmailChange,
  nextPath,
  onCompleted,
  asWorkspaceAdmin = false,
  fields = ["email", "password"],
  cta = "Create Account",
}: SignUpFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState<string>("");
  const [giftCode, setGiftCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [tosChecked, setTosChecked] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (error: Error) => {
    setLoading(false);
    if (error.message === "invalid email") {
      setError("Please enter a valid email address.");
    } else if (error.message === "this email already exists") {
      setError("This email already exists, try logging in.");
    } else if (error.message.includes("gift code")) {
      setError(error.message);
    } else if (error.message.includes("Workspace already has members")) {
      setError(
        "This Workspace already has users in it. You are not allow to signup to this workspace",
      );
    } else {
      setError(
        "Something went very wrong. We are looking into this ASAP. Please try again later.",
      );
      sendErrorToSentry(error, email);
    }
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleComplete = (response: SignUpMutation) => {
    try {
      handleSignUpComplete({
        response,
        handleError,
        nextPath,
        onCompleted,
        asWorkspaceAdmin,
        navigate,
        searchParams,
      });
    } catch (error) {
      handleError(error as Error);
    }
  };

  const { data: csrfData } = useQuery(GetCSRFQuery);

  const [submit] = useSignUpMutation({
    variables: getSignUpParams(
      email,
      name,
      password,
      giftCode,
      asWorkspaceAdmin,
    ),
    onCompleted: handleComplete,
    onError: handleError,
  });

  const canSubmit =
    (!!name || !fields.includes("name")) &&
    (!!email || !fields.includes("email")) &&
    (!!password || !fields.includes("password")) &&
    (!!giftCode || !fields.includes("giftCode")) &&
    tosChecked;

  function handleSubmit() {
    submit();
    setLoading(true);
  }

  return (
    <div className="space-y-6">
      <input
        hidden={true}
        name="X-CSRF-TOKEN"
        readOnly={true}
        value={csrfData && csrfData.csrf}
      />

      {fields.includes("name") && (
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <div className="relative">
            <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="name"
              name="name"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              autoFocus={true}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
              className="pl-10 h-10"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <MailIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="you@gmail.com"
            value={email}
            onChange={(e) => {
              setError(null);
              const { value } = e.target;
              setEmail(value);
              if (onEmailChange) {
                onEmailChange(value);
              }
            }}
            disabled={loading}
            autoFocus={fields[0] === "email"}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
            className="pl-10 h-10"
          />
        </div>
      </div>

      {fields.includes("password") && (
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <KeyIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Enter your password..."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
              className="pl-10 h-10"
            />
          </div>
        </div>
      )}

      {fields.includes("giftCode") && (
        <div className="space-y-2">
          <Label htmlFor="giftCode">Gift Code</Label>
          <div className="relative">
            <KeyIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="giftCode"
              name="giftCode"
              placeholder="Enter gift code"
              value={giftCode}
              onChange={(e) => {
                setError(null);
                setGiftCode(e.target.value);
              }}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center space-x-3 rounded-md border p-2.5">
        <Checkbox
          id="tos"
          checked={tosChecked}
          onCheckedChange={(checked) => setTosChecked(checked as boolean)}
          disabled={loading}
        />
        <Label
          htmlFor="tos"
          className={cn(
            "text-sm font-normal cursor-pointer",
            loading && "opacity-50 cursor-not-allowed",
          )}
        >
          I agree to the{" "}
          <a
            target="_BLANK"
            href="https://www.exec.com/tos"
            className="text-primary hover:underline"
            tabIndex={-1}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            target="_BLANK"
            href="https://www.exec.com/privacy-policy"
            className="text-primary hover:underline"
            tabIndex={-1}
          >
            Privacy Policy
          </a>
        </Label>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!canSubmit || loading}
        className="w-full"
      >
        {cta}
      </Button>
    </div>
  );
}
